<template>
  <MDBRow v-if="training">
    <MDBCol cols="12" lg="7">
      <div class="textBox textBox--small">
        <div class="fw-bold pb-2">
          {{ $t("trainings.singleLecture.registerForm.title") }}
        </div>
        <div v-if="training" v-html="description"></div>
      </div>
    </MDBCol>
    <MDBCol cols="12" lg="5" v-if="!result.success && !isWholesaler && !isWholesalerStaff">
      <VeeForm class="form user-form" @submit="onSubmit">
        <p class="fw-bold">
          {{ $t("trainings.singleLecture.registerForm.inquiry") }}
        </p>
        <p>
          {{ $t("trainings.singleLecture.registerForm.interested") }}
          <span class="fw-bold">{{ getLocalizedValue(training.title) }}</span>
        </p>
        <div v-for="trainingType in trainingTypes" :key="trainingType.id"
          :class="`${trainingDetails.length < 2 ? 'd-none' : ''}`">
          <input class="form-check-input" type="radio" name="trainingType" :id="`trainingType-${trainingType.id}`"
            v-model="selectedTrainingType" :value="trainingType.id" :disabled="trainingDetails.length < 2" />
          <label class="form-check-label" :for="`trainingType-${trainingType.id}`">{{
            getLocalizedValue(trainingType.name)
            }}</label>
        </div>
        <div>
          <p>
            {{ $t("trainings.singleLecture.registerForm.participantsNr") }}
          </p>
          <div>
            <Field type="number" class="form-control"
              :placeholder="$t('trainings.singleLecture.registerForm.participantsNr')"
              :aria-label="$t('trainings.singleLecture.registerForm.participantsNr')" aria-describedby="basic-addon1"
              v-model="participantCount" :rules="selectedTrainingDetail?.b_training_cost_id === 2
                ? {}
                : { min_value: min_participants, max_value: max_participants }" name="participantCount"
              :label="$t('trainings.singleLecture.registerForm.participantsNr')"
              :disabled="selectedTrainingDetail?.b_training_cost_id === 2" />
            <ErrorMessage name="participantCount" class="text-danger text-small text-end m-0 pt-1" />
          </div>
        </div>
        <p class="fw-bold" v-if="canUsePoints">
          {{ $t("trainings.singleLecture.registerForm.pointsFromBalance") }}
        </p>
        <div v-if="canUsePoints">
          <Field type="number" class="form-control" :placeholder="$t('bankstatement.points')"
            :aria-label="$t('bankstatement.points')" aria-describedby="basic-addon1" v-model="setPoints"
            name="setPoints" :rules="{ min_value: 0, max_value: maxPoints, required: only_points_payment }"
            :label="$t('bankstatement.points')" :disabled="!canUsePoints || isStaff" />
          <ErrorMessage name="setPoints" class="text-danger text-small text-end m-0 pt-1" />
        </div>
        <p v-if="calculatePriceDiscount > 0 && canUsePoints">
          {{
            $t("trainings.singleLecture.registerForm.reduction", {
              points: calculatePriceDiscount,
            })
          }}
        </p>
        <div class="pt-3">
          <button type="submit" class="btn btn-danger text-uppercase" :disabled="isStaff || isPending">
            {{ $t("trainings.singleLecture.registerForm.request") }}
          </button>
        </div>

        <p v-if="only_points_payment && !hasEnoughPoints">
          <span class="text-danger">{{ $t("trainings.singleLecture.registerForm.notEnoughPoints") }}</span>
        </p>
      </VeeForm>
    </MDBCol>
    <Success v-else-if="result.success && !isWholesaler && !isWholesalerStaff" />
  </MDBRow>
</template>

<script setup>
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import Success from "./Success.vue";
import { MDBRow, MDBCol } from "mdb-vue-ui-kit";
import { defineProps, defineEmits, ref, computed, watch } from "vue";
import i18n from "@/plugins/i18n";
import { useStore } from "vuex";
import { pickTranslation } from "@/utils/pickTranslation";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
const store = useStore();

defineProps({
  trainingTypes: Array,
});
const emit = defineEmits(["refresh"]);

const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);
const isPending = ref(false);
const selectedTrainingType = ref(1);
const setPoints = ref(0);
const participantCount = ref(1);
const user = computed(() => store.getters.getMe);
const profile = computed(() => store.getters.getProfile);
const trainingDetails = computed(() => store.getters.getBTrainingDetails);
const maxPoints = computed(() => {
  const points = user.value?.points;
  if (typeof points === "number") return points;
  return Number(points?.avail_points ?? 0);
});
const result = computed(() => store.getters.getBTrainingResult);
const training = computed(() => store.getters.getBTraining);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const getLocalizedValue = (value) => pickTranslation(value, currentLocale.value);
const profileGroupId = computed(() => profile.value?.group_id);
const canUsePoints = computed(() =>
  Boolean(
    training.value?.id &&
    Array.isArray(training.value.can_spend_points) &&
    profileGroupId.value &&
    training.value.can_spend_points.includes(profileGroupId.value)
  )
);
const description = computed(() => {
  if (!training.value?.id) return "";
  return canUsePoints.value
    ? training.value.description?.[currentLocale.value] || ""
    : training.value.default_description?.[currentLocale.value] || "";
});
const only_points_payment = computed(() => Boolean(training.value?.only_points_payment))
const selectedTrainingDetail = ref(null)
const min_participants = ref(0)
const max_participants = ref(0)

const toLocalizedNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value) || 0;
  if (value && typeof value === "object") {
    return Number(getLocalizedValue(value)) || 0;
  }
  return 0;
};

const calculatePriceDiscount = computed(() => {
  return Math.round(setPoints.value * 0.25);
});

watch(user, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isWholesaler.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesalerStaff")) {
    isWholesalerStaff.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "staff")) {
    isStaff.value = true;
  }
});

const onSubmit = async () => {
  isPending.value = true;
  const trainingDetail = trainingDetails.value.find(
    (el) => el.b_training_type_id == selectedTrainingType.value
  );
  if (!trainingDetail) return;

  const participantNumber = trainingDetail.b_training_cost_id === 2
    ? 1
    : Number(participantCount.value || 0);

  if (participantNumber < 1) {
    toast(i18n.global.t("global.validation.requiredField"), {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
    return;
  }

  const trainingPrice = toLocalizedNumber(trainingDetail.price);
  const priceValidation = trainingDetail.b_training_cost_id == 1
    ? participantNumber * trainingPrice * 4
    : trainingPrice * 4;

  if (only_points_payment.value && maxPoints.value < priceValidation) {
    toast(i18n.global.t("toast.canAddProduct"), {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } else {
    const objData = Object.assign({}, trainingDetail);

    let data = {
      user_id: user.value.id,
      b_training_id: training.value.id,
      b_training_type_id: selectedTrainingType.value,
      b_training_cost_id: objData.b_training_cost_id,
      participant_number: participantNumber,
      used_points: setPoints.value,
      price: calcPrice(objData),
    };
    const response = await store.dispatch("storeBResult", data);

    if (response === true) {
      toast(i18n.global.t("trainings.singleLecture.success.title"), {
        type: "success",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      emit("refresh");
      return;
    }

    const backendMessage =
      response?.errors?.participant_number?.[0] ||
      response?.message ||
      i18n.global.t("toast.defaultError");

    toast(backendMessage, {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });

  }
  isPending.value = false;
};

const calcPrice = (objData) => {
  const fullPrice =
    selectedTrainingType.value == 1
      ? toLocalizedNumber(objData.price) * participantCount.value
      : toLocalizedNumber(objData.price);

  return fullPrice - calculatePriceDiscount.value;
};

watch(() => participantCount.value, (newVal) => {
  if (newVal && newVal >= 0) {
    const trainingDetail = selectedTrainingDetail.value;

    if (only_points_payment.value && trainingDetail) {
      setPoints.value = toLocalizedNumber(trainingDetail.price) * 4 * newVal;
    }
  }
});

const syncSelectedTrainingDetail = () => {
  if (!trainingDetails.value || trainingDetails.value.length === 0) return;

  if (trainingDetails.value.length < 2) {
    selectedTrainingType.value = trainingDetails.value[0].b_training_type_id;
  }

  let detail = trainingDetails.value.find(
    (el) => el.b_training_type_id == selectedTrainingType.value
  );

  if (!detail) {
    selectedTrainingType.value = trainingDetails.value[0].b_training_type_id;
    detail = trainingDetails.value.find(
      (el) => el.b_training_type_id == selectedTrainingType.value
    );
  }

  selectedTrainingDetail.value = detail || null;

  if (!detail) {
    min_participants.value = 0;
    max_participants.value = 0;
    return;
  }

  const isGroupType = detail.b_training_cost_id === 2;
  min_participants.value = isGroupType
    ? 1
    : toLocalizedNumber(detail.min_participant);
  max_participants.value = isGroupType
    ? 1
    : toLocalizedNumber(detail.max_participant);

  if (isGroupType) {
    participantCount.value = 1;
  } else if (!participantCount.value || participantCount.value < min_participants.value) {
    participantCount.value = min_participants.value || 1;
  }
};

watch([trainingDetails, selectedTrainingType, currentLocale], syncSelectedTrainingDetail, {
  immediate: true,
});
</script>
